import { CSSProperties, useContext } from 'react';
import TableSettingsContext, {
  TABLE_VIEW_SCALE_DEFAULT,
  TABLE_VIEW_SCALE_MAX,
  TABLE_VIEW_SCALE_MIN,
  TABLE_VIEW_SCALE_STEP
} from 'contexts/TableSettingsContext';

/**
 * The "Table View Size" slider, its readout and its reset.
 *
 * ONE component because the control is reachable from two places — the in-table
 * hamburger (TableMenuPanel) and the account menu (AccountMenuPanel) — and a
 * player who changes it in one expects the other to agree. It shipped as a
 * copy-paste of the markup and its three style objects into both panels, which
 * is exactly how the two drift: the next change to the format, the label or the
 * disabled state lands in one panel and not the other.
 *
 * The readout is a PERCENTAGE OF THE DEFAULT rather than an absolute number.
 * "120%" invites "of what?" — of the design size, of the container, of the last
 * setting? — while "Default" answers it. The reset exists because the slider had
 * neither a value nor a way back: a player who dragged it once had no way to
 * tell where they were or what they had left, which is the "players who land on
 * Smallest should not be stuck there without knowing why" requirement.
 */
export const TableViewSizeControl = ({ testIdPrefix }: { testIdPrefix: string }) => {
  const { tableViewScale, setTableViewScale, resetTableViewScale } =
    useContext(TableSettingsContext);
  const isDefault = tableViewScale === TABLE_VIEW_SCALE_DEFAULT;

  return (
    <>
      <div style={sliderTrackRow}>
        <span style={sliderEndLabel}>Small</span>
        <input
          type='range'
          aria-label='Table View Size'
          min={TABLE_VIEW_SCALE_MIN}
          max={TABLE_VIEW_SCALE_MAX}
          step={TABLE_VIEW_SCALE_STEP}
          value={tableViewScale}
          onChange={(e) => setTableViewScale(Number(e.target.value))}
          style={sliderInputStyle}
        />
        <span style={sliderEndLabel}>Largest</span>
      </div>
      <div style={sliderReadoutRow}>
        <span style={sliderReadoutValue} data-testid={`${testIdPrefix}-readout`}>
          {isDefault
            ? 'Default'
            : `${Math.round((tableViewScale / TABLE_VIEW_SCALE_DEFAULT) * 100)}%`}
        </span>
        <button
          type='button'
          onClick={resetTableViewScale}
          disabled={isDefault}
          data-testid={`${testIdPrefix}-reset`}
          style={{
            ...sliderResetButton,
            opacity: isDefault ? 0.4 : 1,
            cursor: isDefault ? 'default' : 'pointer'
          }}>
          Reset
        </button>
      </div>
    </>
  );
};

export default TableViewSizeControl;

const sliderTrackRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginTop: 10,
  width: '100%'
};

const sliderEndLabel: CSSProperties = {
  fontSize: '0.62rem',
  color: 'rgba(255,255,255,0.5)',
  flexShrink: 0,
  letterSpacing: '0.04em'
};

/** Identical in both panels before the extraction — carried over unchanged. */
const sliderInputStyle: CSSProperties = {
  flex: 1,
  accentColor: '#E98C26',
  cursor: 'pointer',
  height: 4,
  minWidth: 0
};

const sliderReadoutRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  marginTop: 6,
  width: '100%'
};

const sliderReadoutValue: CSSProperties = {
  fontSize: '0.68rem',
  color: 'rgba(255,255,255,0.72)',
  letterSpacing: '0.04em',
  // Tabular figures so the row does not jitter as the player drags.
  fontVariantNumeric: 'tabular-nums'
};

const sliderResetButton: CSSProperties = {
  background: 'none',
  border: '1px solid rgba(255,255,255,0.22)',
  borderRadius: 6,
  color: 'rgba(255,255,255,0.78)',
  fontSize: '0.62rem',
  letterSpacing: '0.06em',
  padding: '3px 10px',
  textTransform: 'uppercase'
};
